/**
 * ReplyComposer — paste a DM you received, get 3 Inea-crafted replies.
 *
 * Lives inside the prospect detail drawer. Collapsed by default; expands when
 * the user wants help with a conversation. Each suggestion has a Copy button.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MessageCircle, Sparkles, Loader2, Copy, Check, ChevronDown, Send, Instagram, AlertCircle } from 'lucide-react';
import { useReplySuggestions, type ReplySuggestion } from '@/hooks/useProspect';
import { apiRequest, ApiError } from '@/lib/api';
import { useFeatures } from '@/hooks/useFeatures';

interface SendDmResponse {
  ok: boolean;
  interaction_id?: string;
  message_id?: string;
}

/** Sends a DM via the Meta Graph API. Server validates everything. */
function useSendDM(prospectId: string) {
  const qc = useQueryClient();
  return useMutation<SendDmResponse, ApiError, { text: string }>({
    mutationFn: (body) =>
      apiRequest<SendDmResponse>(`/prospect/${prospectId}/send-dm`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['timeline', prospectId] });
    },
  });
}

interface ReplyComposerProps {
  prospectId: string;
}

export function ReplyComposer({ prospectId }: ReplyComposerProps) {
  const { t } = useTranslation();
  const { meta_features_enabled: metaEnabled } = useFeatures();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [suggestions, setSuggestions] = useState<ReplySuggestion[]>([]);
  const mutation = useReplySuggestions(prospectId);

  function ask(): void {
    if (mutation.isPending || message.trim().length < 2) return;
    setSuggestions([]);
    mutation.mutate(message.trim(), {
      onSuccess: (data) => setSuggestions(data.suggestions),
    });
  }

  return (
    <section className="rounded-2xl border border-violet/30 bg-gradient-to-br from-violet/[0.04] to-pink/[0.03]">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left"
      >
        <span className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-violet to-pink text-white">
            <MessageCircle size={15} />
          </span>
          <span>
            <span className="font-display text-[14px] font-800 tracking-[-0.015em]">
              {t('reply.title')}
            </span>
            <span className="ml-2 font-mono text-[10px] font-700 uppercase tracking-[0.12em] text-violet">
              IA
            </span>
            <p className="mt-0.5 text-[12px] text-ink/55">
              {t('reply.subtitle')}
            </p>
          </span>
        </span>
        <ChevronDown
          size={16}
          className={`shrink-0 text-ink/45 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                maxLength={4000}
                placeholder={t("reply.messagePlaceholder")}
                className="w-full resize-none rounded-xl border border-ink/[0.12] bg-white px-3 py-2.5 text-[13px]
                           leading-[1.5] text-ink outline-none transition focus:border-violet/60 focus:ring-2 focus:ring-violet/20"
              />
              <button
                onClick={ask}
                disabled={mutation.isPending || message.trim().length < 2}
                className="btn-primary mt-3 w-full justify-center text-[13px] disabled:opacity-50"
              >
                {mutation.isPending ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Inea réfléchit…
                  </>
                ) : (
                  <>
                    <Sparkles size={14} /> Suggérer 3 réponses
                  </>
                )}
              </button>

              {mutation.isError && (
                <p className="mt-2 rounded-lg bg-pink/10 px-3 py-2 text-[12px] font-600 text-pink-deep">
                  {mutation.error instanceof ApiError ? mutation.error.message : t('common.errorGeneric')}
                </p>
              )}

              {suggestions.length > 0 && (
                <div className="mt-3 space-y-2.5">
                  {suggestions.map((s, i) => (
                    <SuggestionCard key={i} suggestion={s} prospectId={prospectId} />
                  ))}
                </div>
              )}

              {metaEnabled && <InstagramDirectSend prospectId={prospectId} />}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function SuggestionCard({
  suggestion,
  prospectId,
}: {
  suggestion: ReplySuggestion;
  prospectId: string;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  // Lot A (2026-07-20) — META masqué : le bouton « Envoyer » (DM Instagram)
  // disparaît, le mode copier-coller reste.
  const { meta_features_enabled: metaEnabled } = useFeatures();
  const send = useSendDM(prospectId);

  function copy(): void {
    void navigator.clipboard.writeText(suggestion.text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  }

  function sendIt(): void {
    if (send.isPending) return;
    send.mutate({ text: suggestion.text });
  }

  return (
    <div className="rounded-xl border border-ink/[0.08] bg-white/85 p-3.5">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="font-mono text-[10px] font-700 uppercase tracking-[0.12em] text-violet">
          {suggestion.angle}
        </span>
        <div className="flex items-center gap-1.5">
          <button
            onClick={copy}
            className="inline-flex items-center gap-1 rounded-lg bg-ink/[0.05] px-2 py-1 text-[11px] font-700 text-ink/60 transition hover:bg-violet/10 hover:text-violet"
          >
            {copied ? <Check size={11} /> : <Copy size={11} />}
            {copied ? 'Copié' : 'Copier'}
          </button>
          {metaEnabled && (
          <button
            onClick={sendIt}
            disabled={send.isPending || send.isSuccess}
            className="inline-flex items-center gap-1 rounded-lg bg-gradient-to-br from-violet to-pink px-2 py-1 text-[11px] font-700 text-white transition hover:opacity-90 disabled:opacity-50"
            title={t('reply.sendDmTitle')}
          >
            {send.isPending ? (
              <Loader2 size={11} className="animate-spin" />
            ) : send.isSuccess ? (
              <Check size={11} />
            ) : (
              <Send size={11} />
            )}
            {send.isPending ? 'Envoi…' : send.isSuccess ? 'Envoyé' : 'Envoyer'}
          </button>
          )}
        </div>
      </div>
      <p className="text-[13px] leading-[1.55] text-ink/85">{suggestion.text}</p>
      {send.isError && (
        <p className="mt-2 flex items-start gap-1.5 rounded-md bg-pink/10 px-2 py-1.5 text-[11px] font-600 text-pink-deep">
          <AlertCircle size={12} className="mt-0.5 shrink-0" />
          <span>{send.error?.message ?? 'Échec de l\'envoi.'}</span>
        </p>
      )}
    </div>
  );
}

/**
 * Standalone composer to send a custom DM straight from the prospect drawer.
 * Used for one-off outreach (vs. the AI-suggested reply cards above).
 *
 * Meta requires the prospect to have messaged us first — the backend returns
 * 409 / no_recipient_id otherwise, and we surface that clearly here.
 */
function InstagramDirectSend({ prospectId }: { prospectId: string }) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const send = useSendDM(prospectId);

  function onSend(): void {
    if (send.isPending || text.trim().length < 2) return;
    send.mutate(
      { text: text.trim() },
      {
        onSuccess: () => setText(''),
      },
    );
  }

  return (
    <div className="mt-4 rounded-xl border border-pink/30 bg-gradient-to-br from-pink/[0.04] to-violet/[0.03] p-3.5">
      <div className="mb-2 flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-pink to-violet text-white">
          <Instagram size={13} />
        </span>
        <span className="font-display text-[13px] font-800 tracking-[-0.015em]">
          Envoyer un DM Instagram
        </span>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={2}
        maxLength={1000}
        placeholder={t('reply.instagramPlaceholder')}
        className="block w-full rounded-lg border border-ink/[0.10] bg-white px-3 py-2 text-[13px] leading-[1.5] text-ink outline-none transition focus:border-pink/60 focus:ring-2 focus:ring-pink/20"
      />
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="font-mono text-[10px] font-700 text-ink/40">
          {text.length}/1000
        </span>
        <button
          onClick={onSend}
          disabled={send.isPending || text.trim().length < 2}
          className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-br from-pink to-violet px-3.5 py-2 text-[12.5px] font-700 text-white transition hover:opacity-90 disabled:opacity-50"
        >
          {send.isPending ? (
            <>
              <Loader2 size={13} className="animate-spin" /> Envoi…
            </>
          ) : send.isSuccess ? (
            <>
              <Check size={13} /> Envoyé
            </>
          ) : (
            <>
              <Send size={13} /> Envoyer
            </>
          )}
        </button>
      </div>
      {send.isError && (
        <p className="mt-2 flex items-start gap-1.5 rounded-md bg-pink/10 px-2 py-1.5 text-[11px] font-600 text-pink-deep">
          <AlertCircle size={12} className="mt-0.5 shrink-0" />
          <span>{send.error?.message ?? 'Échec de l\'envoi.'}</span>
        </p>
      )}
    </div>
  );
}
